import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Clock, User, Zap, Pin, Loader2, GitBranch, List } from 'lucide-react';
import { useLineItemTasks, type LineItemTask } from '../hooks/useLineItemTasks';
import { useTaskDependencies } from '../hooks/useTaskDependencies';
import { TaskFlowChart } from './TaskFlowChart';
import { formatMinutes } from '../lib/utils';
import { safeFormatDate } from '../lib/utils';
import type { TaskStatus } from 'scheduler-types';

const taskStatusColors: Record<TaskStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  ready: 'bg-yellow-100 text-yellow-800',
  staged: 'bg-purple-100 text-purple-700',
  partial_staged: 'bg-orange-100 text-orange-700',
  in_progress: 'bg-blue-100 text-blue-700',
  complete: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  abandoned: 'bg-gray-100 text-gray-500',
};

function TaskStatusBadge({ status }: { status: TaskStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${taskStatusColors[status]}`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function TaskRow({ task }: { task: LineItemTask }) {
  const estimateRange =
    task.estimatedMinutesMin === task.estimatedMinutesMax
      ? formatMinutes(task.estimatedMinutesMin)
      : `${formatMinutes(task.estimatedMinutesMin)} - ${formatMinutes(task.estimatedMinutesMax)}`;

  return (
    <div className="flex items-center justify-between p-3 bg-background-subtle rounded-lg">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm text-foreground truncate">
            {task.laborBucket.replace(/_/g, ' ')}
          </p>
          {task.isExpediteEligible && (
            <span title="Expedite eligible"><Zap className="h-3.5 w-3.5 text-warning" /></span>
          )}
          {task.isPinned && (
            <span title="Pinned assignment"><Pin className="h-3.5 w-3.5 text-primary" /></span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-foreground-muted mt-1">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {estimateRange}
            {task.actualMinutes !== null && (
              <span className="text-foreground">({formatMinutes(task.actualMinutes)} actual)</span>
            )}
          </span>
          {task.assignedPersonName && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {task.assignedPersonName}
            </span>
          )}
          {task.scheduledStart && (
            <span className="text-foreground-muted">
              Scheduled: {safeFormatDate(task.scheduledStart, 'MMM d, yyyy')}
            </span>
          )}
        </div>
      </div>
      <TaskStatusBadge status={task.status} />
    </div>
  );
}

interface LineItemTasksProps {
  lineItemId: string;
}

export function LineItemTasks({ lineItemId }: LineItemTasksProps) {
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'flow'>('flow');
  const { data: tasks, isLoading, error } = useLineItemTasks(expanded ? lineItemId : undefined);

  // Extract task IDs for dependency fetching (only when in flow view and tasks are loaded)
  const taskIds = useMemo(
    () => (tasks && tasks.length > 0 ? tasks.map((t) => t.id) : undefined),
    [tasks]
  );

  const {
    data: tasksWithDeps,
    isLoading: depsLoading,
    error: depsError,
  } = useTaskDependencies(viewMode === 'flow' && expanded ? taskIds : undefined);

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center gap-2 text-sm text-primary hover:text-primary-hover transition-colors mt-3"
      >
        <ChevronRight className="h-4 w-4" />
        Show tasks
      </button>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setExpanded(false)}
          className="flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
        >
          <ChevronDown className="h-4 w-4" />
          Hide tasks
        </button>

        {/* View mode toggle */}
        {tasks && tasks.length > 1 && (
          <div className="flex items-center gap-1 bg-background-muted rounded-md p-0.5">
            <button
              onClick={() => setViewMode('flow')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === 'flow'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
              title="Dependency flow chart"
            >
              <GitBranch className="h-3.5 w-3.5" />
              Flow
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
              title="Task list"
            >
              <List className="h-3.5 w-3.5" />
              List
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-foreground-muted py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading tasks...
        </div>
      ) : error ? (
        <div className="text-sm text-danger py-2">
          Failed to load tasks: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      ) : tasks && tasks.length > 0 ? (
        <>
          {/* Flow chart view */}
          {viewMode === 'flow' && (
            depsLoading ? (
              <div className="flex items-center gap-2 text-sm text-foreground-muted py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading dependency graph...
              </div>
            ) : depsError ? (
              <div className="text-sm text-danger py-2">
                Failed to load dependency graph: {depsError instanceof Error ? depsError.message : 'Unknown error'}
              </div>
            ) : tasksWithDeps && tasksWithDeps.length > 0 ? (
              <TaskFlowChart tasks={tasksWithDeps} />
            ) : (
              <div className="text-sm text-foreground-muted py-2">
                No dependency data available. <button onClick={() => setViewMode('list')} className="text-primary hover:underline">Switch to list view</button>
              </div>
            )
          )}

          {/* List view */}
          {viewMode === 'list' && (
            <div className="space-y-2">
              {tasks.map((task) => (
                <TaskRow key={task.id} task={task} />
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="text-sm text-foreground-muted py-2">
          No tasks for this line item.
        </div>
      )}
    </div>
  );
}
